import { ComponentTypeHierarchies } from 'streams/componentTypeHierarchies/componentTypeHierarchyOperations';
import type {
  ApiCountInstancesRequest,
  APICountInstancesResponse,
  APIViewpointAttributes,
  BooleanOperator,
  ComponentSelection,
  DirectedTriple,
  DirectedTripleWithFilters,
  APIFieldGlobalAttributes,
  MetaModel,
  MetaModelReferenceType,
  Operator,
  PathCollapsingRule,
  QueryBuilderQuery,
  QueryBuilderRuleValue,
  TraversalPathMatchingType,
  TripleDirection,
  ViewpointBuilderFilterPerNode,
  ViewpointBuilderFilters,
  APIFieldAttributes,
  MetaModelTriple,
  LinkedComponent,
  StartSetExpansion,
} from '@ardoq/api-types';
import type { ReferenceGraphData } from '@ardoq/data-model';
import type { SolutionMetadata } from '@ardoq/core';
import type {
  CollapsibleGraphGroup,
  GraphItemId,
  ReferenceTypeRepresentationData,
  MetamodelViewGraphEdge,
  MetaModelViewGraphNode,
} from '@ardoq/graph';
import type { ComponentRepresentationDataWithColorShades } from '@ardoq/basic-components';
import type { BlockDiagramViewSettings } from 'tabview/blockDiagram/types';
import type { GraphInterface } from './components/SimpleGraph/GraphInterface';
import type { IconName } from '@ardoq/icons';
import type {
  ViewpointBuilderNavigationViewModel,
  ViewpointBuilderTab,
} from './viewpointBuilderNavigation/types';
import type {
  ComponentSearchData,
  ComponentSearchViewState,
} from 'viewpointBuilder/selectContextTab/types';
import type { MetamodelViewState } from './metaModel/types';
import type { MetamodelViewCommands } from './metaModel/metamodelViewCommands';
import type { ComponentsSearchCommands } from 'viewpointBuilder/selectContextTab/commands';
import type { ViewpointBuilderFiltersCommands } from './addFiltersTab/viewpointBuilderFiltersCommands';
import type { ViewpointBuilderNavigationCommands } from './viewpointBuilderNavigation/viewpointBuilderNavigationCommands';
import type { EditTraversalCommands } from './traversals/editTraversalCommands';
import type {
  AsyncLabelLoaders,
  AsyncSuggestionsLoaders,
  QueryBuilderFilterDefinition,
} from '@ardoq/query-builder';
import type { QueryBuilderFilterDefinitionGroups } from './addFiltersTab/composeViewpointBuilderComponentsFilterGroups';
import type { MetaInfoState } from './viewpointMetaInfo/metaInfoTypes';
import type { MetaInfoCommands } from './viewpointMetaInfo/metaInfoCommands';
import type { ViewpointGroupingRulesCommands } from './addGroupingRulesTab/viewpointGroupingRulesCommands';
import type { GroupingRulesState } from './addGroupingRulesTab/groupingRulesTypes';
import type { ViewpointBuilderFormattingCommands } from './formattingTabs/viewpointBuilderFormattingCommands';
import type { PrimaryButtonConfig } from './openViewpointBuilder/getPrimaryButtonConfig';
import type {
  EditablePathCollapsingRule,
  PathCollapsingViewState,
} from './collapsePathsTab/types';
import type { CollapsePathsCommands } from './collapsePathsTab/collapsePathsCommands';
import type { ViewpointFormattingProps } from './formattingTabs/viewpointFormattingTypes';
import type { RequiredComponentsCommands } from './requiredComponentsTab/requiredComponentsCommands';
import type { RequiredComponentsViewState } from './requiredComponentsTab/types';
import type { SelectedViewCommands } from './selectedView/commands';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';

/**
 * A component or reference id.
 * */
type EntityId = string;
/**
 * A id used in the selected graph. The selected graph can contain several
 * instances of a component or reference type.
 */

export type GraphId = string;

export type TripleMap = Map<
  string,
  { sourceId: EntityId; referenceId: EntityId; targetId: EntityId }
>;

export type MetadataComponent = {
  representationData: ComponentRepresentationDataWithColorShades;
  color: string;
  typeId: string;
  label: string;
  /** Optional hover tooltip text. */
  tooltip?: string;
};

export type MetaDataReference = {
  representationData: ReferenceTypeRepresentationData;
};

export type NamedDirectedTriple = {
  direction: TripleDirection;
  sourceType: string;
  referenceType: string;
  targetType: string;
};

export type BaseTripleOption = {
  tripleId: string;
  isSelected: boolean;
  metaDataComponent: MetadataComponent;
  metaDataReference: MetaDataReference;
  namedDirectedTriple: NamedDirectedTriple;
};

export type TripleOption = BaseTripleOption & {
  countKey: string;
  instanceCounts: number | null;
  isDisabled: boolean;
  togglingDisabledExplanation?: string;
};

export type TripleOptions = {
  incoming: TripleOption[];
  outgoing: TripleOption[];
  instanceCountsResponse: APICountInstancesResponse | null;
  selectedNode: {
    metaDataComponent: MetadataComponent;
  };
  selectedEdge?: {
    metaDataComponent: MetaDataReference;
  };
  fetchCountsArgs: ApiCountInstancesRequest | null;
  optionCountLoadingState: 'idle' | 'loading' | 'loaded' | 'error';
  showOnlyOptionsWithInstanceCounts: boolean; // TODO AM do we need this one?
};

export type SelectedGraphModelMap = Map<
  string,
  (LinkedComponent & { tripleId: string })[]
>;

export type SelectedGraph = {
  sourceMap: SelectedGraphModelMap;
  targetMap: SelectedGraphModelMap;
  referenceMap: Map<
    GraphId,
    {
      sourceId: GraphId;
      targetId: GraphId;
      tripleId: string;
    }
  >;
};

export type RawGraphNode = {
  modelId: EntityId;
  label: string;
  metaData: MetadataComponent;
};

export type RawGraphEdge = {
  modelId: EntityId;
  label: string;
  sourceId: EntityId;
  targetId: EntityId;
  metaData: MetaDataReference;
};

/**
 * This map holds the data needed to display the components nicely.
 */
export type RawGraphNodes = Map<EntityId, RawGraphNode>;

/**
 * This map holds the data needed to display the references nicely.
 */
export type RawGraphEdges = Map<EntityId, RawGraphEdge>;

export type ExpandableSectionId = 'outgoingReferences' | 'incomingReferences';

export type InstanceCounts = {
  namedDirectedTriplePath: DirectedTriple[];
  graphNodeId: GraphId;
  pathKey: string;
  label: string;
  counts?: number;
};

export type ViewpointBuilderFilter = {
  nonPersistentId: string;
  graphNodeId: GraphId;
  filterBy: string;
  operator: Operator;
  value: QueryBuilderRuleValue | undefined;
  filterDefinition: QueryBuilderFilterDefinition;
};

export type TraversalBuilderFiltersPerNode = {
  filters: ViewpointBuilderFilter[];
  condition: BooleanOperator;
  localFilterId: string;
  /**
   * Rules the builder has no filter definition for and so cannot render, carried through untouched
   * so that saving does not delete them. A component exclusion is the case this exists for: it
   * filters on `_id`, which is not a user field.
   */
  preservedRules: ViewpointBuilderFilterPerNode['rules'];
};

export type ViewpointBuilderFiltersDefinitions = {
  componentFilterTypeOptions: {
    label: string;
    options: { label: string; value: string }[];
  }[];
  componentsFilterGroups: QueryBuilderFilterDefinitionGroups;
};

export type TraversalBuilderViewState = Omit<
  TraversalBuilderState,
  'triplesSortAndFiltersState'
> & {
  triplesSortAndFiltersState: TriplesSortAndFiltersViewState;
  filtersDefinitions: ViewpointBuilderFiltersDefinitions;
  asyncLoaders: {
    asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
    asyncLabelLoaders: AsyncLabelLoaders;
  };
  stateAsSavableAttributes: TraversalBuilderAsSavableAttributes;
  perspectivesOptionsArgs: {
    workspaceIds: string[];
    componentTypeNames: string[];
    referenceTypeNames: string[];
    availableComponentFields: APIFieldGlobalAttributes[];
    availableReferenceFields: APIFieldGlobalAttributes[];
    componentFieldsByType: Map<string, APIFieldAttributes[]>;
    referenceFieldsByType: Map<string, APIFieldAttributes[]>;
  };
  pathCollapsingViewState: PathCollapsingViewState;
  shouldShowDeleteCurrentNodeButton: boolean;
  /**
   * Whether the "expand start set with descendants" checkbox should be shown.
   * True only when the START_SET_EXPANSION_WITH_DESCENDANTS feature flag is
   * enabled and the currently selected graph node is the start node.
   */
  shouldShowStartSetExpansionToggle: boolean;
  /**
   * The component types the hierarchy of the start type covers, the start type
   * included, for the "include descendants" card. Shown whether or not the
   * expansion is on: it is what the user needs to decide whether to turn it on.
   */
  hierarchyComponentTypes: MetadataComponent[];
  requiredComponentsState: RequiredComponentsViewState;
  /**
   * We hide the notification if there is only one node in the graph - the context component type
   * even if shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings is true.
   */
  shouldIncludeClickOtherComponentTypeNodesNotification: boolean;
};

export type TraversalBuilderAsSavableAttributes = {
  startSetFilterId: string | null;
  pathMatching: TraversalPathMatchingType;
  paths: DirectedTripleWithFilters[][];
  filters: ViewpointBuilderFilters;
  startType: string | null;
  pathCollapsingRules: PathCollapsingRule[];
  startSetExpansion?: StartSetExpansion;
  /**
   * The concrete component type the expansion started from. Only set alongside
   * {@link startSetExpansion}; `startType` is the `anyType` sentinel once the
   * hierarchy node is in place, so this is what survives a round trip.
   */
  startSetExpansionFromType?: string;
};

export type PathCollapsingState = {
  rules: PathCollapsingRuleInternal[];
  currentEditableRule: EditablePathCollapsingRule | null;
};

export type TriplesSortAndFiltersState = {
  filterTerm: string;
  showOnlyOptionsWithInstanceCounts: boolean;
  tripleSortOrder: TripleSortOrder;
};

export type TriplesSortAndFiltersViewState = TriplesSortAndFiltersState & {
  outgoingTriplesHiddenCount: number;
  incomingTriplesHiddenCount: number;
};

export type TraversalBuilderState = {
  /**
   * Relations are expressed with entity ids.
   * This is the whole meta model graph.
   */
  baseGraph: Omit<ReferenceGraphData, 'referenceTypes'>;
  componentNameAndFieldsIndex: Map<string, string[]>;
  componentNameAndWorkspacesIndex: Map<string, string[]>;
  metaModelTriples: MetaModelTriple[];
  fieldMap: Map<string, APIFieldAttributes>;
  filteredTripleOptions: TripleOptions;
  filters: Record<GraphId, TraversalBuilderFiltersPerNode>;
  /**
   * Maps which hold the graph nodes instances to instantiate the according
   * yFiles graph entities.
   */
  graphEdges: Map<GraphId, MetamodelViewGraphEdge>;
  graphGroups: Map<GraphId, CollapsibleGraphGroup>;
  graphNodes: Map<GraphId, MetaModelViewGraphNode>;
  hasTripleOptions: Record<ExpandableSectionId, boolean>;
  highlightedPaths: Record<string, HighlightedPath[]>;
  initialFilters: ViewpointBuilderFilters | null;
  initialPathCollapsingRules: PathCollapsingRule[] | null;
  initialPaths: DisableableDirectedTripleWithFilters[][] | null;
  instanceCounts: Map<GraphId, InstanceCounts>;
  isMetaModelLoaded: boolean;
  /**
   * The metamodel as the backend sent it, so that `setTraversal` can rebuild
   * the metamodel-derived state when the paths arrive after the metamodel.
   */
  metaModel: MetaModel | null;
  pathCollapsing: PathCollapsingState;
  pathMatching: TraversalPathMatchingType;
  rawGraphEdges: RawGraphEdges;
  rawGraphNodes: RawGraphNodes;
  /**
   * The org's component type hierarchies, derived in `applyMetaModel` from the
   * metamodel as the backend sent it. A lookup rather than a walk over
   * `metaModelTriples`, which carries the edited paths' triples too.
   */
  componentTypeHierarchies: ComponentTypeHierarchies;
  referenceNameAndFieldsIndex: Map<string, string[]>;
  referenceTypes: MetaModelReferenceType[];
  requiredNodeIds: string[];
  /**
   * Relations are expressed with graph ids.
   * This graph may have several
   * instances of a given entity type of the base graph.
   * The according entity id can be resolved with the graphNodes map
   * with `graphNodes.get(graphId).modelId`.
   */
  selectedGraph: SelectedGraph;
  selectedGraphEdgeId: string | null;
  selectedGraphNodeId: string | null;
  /**
   * Stored in user settings - the user can click the "Don't show again" button and set it permanently to false.
   * If user selects a component type, the hint will not be hidden as long as the ViewpointBuilderState is kept.
   */
  shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings: boolean;
  shouldIncludeInstanceCounts: boolean;
  /**
   * Bumped when the data behind the traversal changed without the traversal
   * changing, so cached instance counts need fetching again.
   */
  instanceCountsVersion: number;
  startComponentType: string | null;
  startComponentTypeId: EntityId | null;
  startNode: MetaModelViewGraphNode | null;
  /**
   * When set, the traversal starts from the resolved start set *and* all of its
   * descendants in the component hierarchy. Controlled by the start-set
   * expansion checkbox (gated behind the
   * START_SET_EXPANSION_WITH_DESCENDANTS feature flag). `null` means no
   * expansion.
   */
  /**
   * The counts the last request reported for the start set: the roots the user
   * selected, and the same set expanded with its hierarchy when the expansion is
   * on. Which of them the start node shows depends on whether a recursive
   * collapsing rule has taken the hierarchy.
   */
  startSetCounts: {
    startSetCount: number;
    expandedStartSetCount: number;
  } | null;
  startSetExpansion: StartSetExpansion | null;
  /**
   * Set while {@link startSetExpansion} is active and the start node has been
   * swapped for the synthetic "Context component type" wildcard node. Holds
   * the original concrete start type (so it can be restored when expansion is
   * turned off — the node itself is re-derived via `createStartNode`) plus the
   * hierarchy component types whose triples are unioned into the wildcard node.
   * `null` when no hierarchy-node expansion is in effect.
   */
  startSetExpansionState: {
    originalStartComponentType: string;
    hierarchyComponentTypes: string[];
  } | null;
  /**
   * Start query passed from componentSearch state stream with state routine.
   */
  traversalStartQuery: QueryBuilderQuery | null;
  /**
   * Selected components passed from componentSearch state stream with state routine.
   */
  traversalStartSet: string[] | null;
  tripleOptions: TripleOptions;
  triples: TripleMap;
  triplesSortAndFiltersState: TriplesSortAndFiltersState;
  isInitialPath: boolean;
  locale: Locale;
  wildcardState: {
    startComponentType: string;
    startNode: MetaModelViewGraphNode;
  } | null;
};

export type HighlightedPath = {
  referenceIds: string[];
  componentIds: string[];
  status: 'error' | 'default';
};

export type TraversalBuilderStateWithCurrentEditableRule =
  TraversalBuilderState & {
    pathCollapsing: {
      currentEditableRule: EditablePathCollapsingRule;
    };
  };

export const isTraversalBuilderStateWithCurrentEditableRule = (
  state: TraversalBuilderState
): state is TraversalBuilderStateWithCurrentEditableRule =>
  Boolean(state.pathCollapsing.currentEditableRule);

export type Direction = 'outgoing' | 'incoming';

export type SimpleViewModel = {
  graphNodes: Map<GraphId, MetaModelViewGraphNode>;
  graphEdges: Map<GraphId, MetamodelViewGraphEdge>;
  graphGroups: Map<GraphId, CollapsibleGraphGroup>;
};

export type SimpleGraphViewProps = {
  viewInstanceId?: string;
  viewSettings?: BlockDiagramViewSettings;
  clearEdges?: boolean;
  viewModel: SimpleViewModel;
  label?: string;
  graphInterface?: GraphInterface;
  topRightCornerButton?: {
    label: string;
    iconName?: IconName;
    onClick: () => void;
  } | null;
  hasZoomControls?: boolean;
  /**
   * If set to true the view will have no zoom controls, not be zoomable nor
   * pannable with the mouse.
   */
  isThumbnailView?: boolean;
  /**
   * When this flag is set to true, the graph is automatically re-scaled and
   * re-centered to fit within its container. By default, this flag is enabled.
   * Its primary function is to prevent unnecessary re-scaling and re-centering
   * of the graph when a user simply selects a different node.
   */
  shouldFitToContentOnLayout?: boolean;
  graphInsets?: number;
  i18nLocale?: I18nLocale;
};

export type TripleSortOrder =
  | 'reference_type_a_z'
  | 'component_type_a_z'
  | 'descending_instance_count';

// TODO (chriskr): Remove this type
export type EditTraversalState = TraversalBuilderState;

export type ViewpointBuilderState = {
  viewpointBuilderNavigationState: ViewpointBuilderNavigationViewModel;
  searchComponentsState: ComponentSearchViewState;
  metamodelViewState: MetamodelViewState;
  editTraversalsState: TraversalBuilderViewState;
  metaInfoState: MetaInfoState;
  groupingsState: GroupingRulesState;
  formattingState: ViewpointFormattingProps;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export type ViewpointBuilderViewState = ViewpointBuilderState & {
  primaryButtonConfig: PrimaryButtonConfig;
  namespace: string;
};

export type ViewpointBuilderCommands = {
  componentsSearchCommands: ComponentsSearchCommands;
  metamodelViewCommands: MetamodelViewCommands;
  filterCommands: ViewpointBuilderFiltersCommands;
  navigationCommands: ViewpointBuilderNavigationCommands;
  editTraversalCommands: EditTraversalCommands;
  metaInfoCommands: MetaInfoCommands;
  groupingsCommands: ViewpointGroupingRulesCommands;
  formattingCommands: ViewpointBuilderFormattingCommands;
  collapsePathsCommands: CollapsePathsCommands;
  requiredComponentsCommands: RequiredComponentsCommands;
  selectedViewCommands: SelectedViewCommands;
};

export type ViewpointBuilderContext =
  //  Edit a viewpoint from the viewpoint list.
  | 'editViewpoint'
  // Create new viewpoint from the homepage.
  | 'createViewpoint'
  // Open a viewpoint from the homepage.
  | 'openViewpoint'
  // Edit a subgraph block from the configuration panel.
  | 'editSubgraph'
  // Open the viewpoint builder from context menu.
  | 'contextMenu'
  // Open the viewpoint builder to load a new viewpoint, e.g. 'Explore data' and 'Open more' buttons.
  | 'loadNewViewpoint';

export type OpenViewpointBuilderArgs = {
  activeTab?: ViewpointBuilderTab;
  context?: ViewpointBuilderContext;
  initialConfiguration?: InitialViewpointBuilderConfiguration;
  folderId?: string | null;
};

export type InitialViewpointBuilderConfiguration = Partial<
  Pick<
    APIViewpointAttributes,
    | 'name'
    | 'description'
    | 'viewName'
    | 'startType'
    | 'paths'
    | 'filters'
    | 'groupBys'
    | 'conditionalFormatting'
  >
> & {
  viewpointId?: string | null;
  viewpointVersion?: number | null;
  loadedStateHash?: string;
  initialPaths?: DirectedTripleWithFilters[][];
  startSearchTerm?: string;
  loadedComponents?: ComponentSearchData[];
  componentSelection: ComponentSelection | null; // null for creating and editing viewpoint
  pathMatching?: TraversalPathMatchingType;
  folderId?: string;
  pathCollapsingRules?: PathCollapsingRule[];
  startSetExpansion?: StartSetExpansion;
  startSetExpansionFromType?: string;
  isMainLoadedState?: boolean;
  solutionMetadata?: SolutionMetadata;
};

export type DisableableDirectedTripleWithFilters = DirectedTripleWithFilters & {
  isDisabled?: boolean;
  togglingDisabledExplanation?: string;
};

export type VirtualEdgeState = {
  collapsedEdges: string[];
  /**
   * Not all edges which are part of the collapsed path should be removed on
   * collapsing a path in the viewpoint builder. For more details check the
   * comments of the `updateEdgesToBeRemovedOnCollapsing` method.
   */
  edgesToBeRemovedOnCollapsing: string[];
  virtualEdge: MetamodelViewGraphEdge;
  tripleReplacements: TripleOptionReplacement[];
  /**
   * How many times a specific match for a collapsed path is matched in the
   * paths of the whole graph. In a example:
   *   Paths:
   *     A -> B -> C -> D -> E
   *     A -> B -> C -> D -> F
   *   Collapsing rule: B -> C -> D
   *   Match count: 2
   * This is used to determine if we can removed collapsed edges from the graph
   * when collapsing a path.
   */
  matchCount: number;
};

export type TripleOptionReplacement = {
  /**
   * To specify for which end of the collapsed path this triple is a replacement.
   */
  start: 'start' | 'end';
  /**
   * The first or the last node of the collapsed path depending on the `start`
   * value.
   */
  graphNodeId: GraphItemId;
  /**
   * The triple id of the collapsed start which this is a replacement for.
   */
  tripleId: string;
  /**
   * The direction of the triple which this is a replacement for.
   */
  direction: TripleDirection;
  replacementDirection: TripleDirection;
  tripleReplacement: TripleOption;
};

export type PathCollapsingRuleInternal = PathCollapsingRule & {
  isExpanded: boolean;
  virtualEdgeStates: VirtualEdgeState[];
};
